<template>
  <v-container class="pb-12">
    <h1 class="my-6 text-h4 text-sm-h3">Cookies</h1>

    <p>This website may store information on your computer in the form of "cookies". You can update your preferences for this browser at the <a href="#preferences">bottom of this page</a>.

    <p>We use cookies to help you personalize your online experience. A cookie is a text file that is placed on your hard disk by a web page server. Cookies cannot be used to run programs or deliver viruses to your computer. Cookies are uniquely assigned to you, and can only be read by a web server in the domain that issued the cookie to you.</p>

    <p>You have the ability to accept or decline cookies. Most Web browsers automatically accept cookies, but you can usually modify your browser setting to decline cookies if you prefer. If you choose to decline cookies, you may not be able to fully experience the interactive features of Ellacard. Please see our <a href="/privacy-policy">privacy policy</a> for more information.</p>

    <p>Ellacard may store information on your computer for the following purposes:</p>

    <div class="overline">Basic functionality</div>
    <p>To remember your account</p>

    <div class="overline">Performance</div>
    <p>To collect aggregated, anonymized usage / performance data via Google Analytics</p>

    <div class="overline">Targeting</div>
    <p>To track ad performance and conversions and enable targeted ads on other sites via Google Analytics and Facebook</p>

    <div class="overline">Other (third-party services)</div>
    <p>To provide additional features from third-party services:
      <ul>
        <li>Facebook &mdash; for signing in</li>
        <li>Giphy &mdash; animated GIF and sticker library</li>
        <li>Google &mdash; for signing in</li>
      </ul>
    </p>

    <h1 id="preferences" class="mt-12 mb-6 text-h5 text-sm-h4">Preferences</h1>

    <div v-if="disabled" class="error--text font-italic mb-6"><v-icon class="mr-1" color="error" v-html="mdiAlert" />Cookies have been disabled at the browser level. Some parts of the site may not work.</div>

    <div class="d-flex align-center">
      <v-switch class="mt-n1" disabled hide-details v-model="scope.strict" />
      <div class="overline mr-2">Strictly necessary</div>
      <v-icon
        @click="$confirm({
          title: 'Strictly necessary',
          msg: `These cookies are necessary for the website to function and cannot be switched off. They are usually only set in response to actions made by you which amount to a request for services, such as setting your privacy preferences or signing in. You can set your browser to block or alert you about these cookies, but some parts of the site will not then work.`,
          buttons: [ 'Ok' ],
          primary: 'Ok',
          persistent: false
        })"
        v-html="mdiHelpCircle"
      />
    </div>
    <div class="d-flex align-center">
      <v-switch class="mt-n1" :disabled="disabled" hide-details v-model="scope.performance" />
      <div class="overline mr-2">Performance cookies</div>
      <v-icon
        @click="$confirm({
          title: 'Performance cookies',
          msg: `These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. All information these cookies collect is aggregated and therefore anonymous. If you do not allow these cookies we will not know when you have visited our site, and will not be able to monitor its performance.`,
          buttons: [ 'Ok' ],
          primary: 'Ok',
          persistent: false
        })"
        v-html="mdiHelpCircle"
      />
    </div>
    <div class="mb-6 d-flex align-center">
      <v-switch class="mt-n1" :disabled="disabled" hide-details v-model="scope.targeting" />
      <div class="overline mr-2">Targeting cookies</div>
      <v-icon
        @click="$confirm({
          title: 'Targeting cookies',
          msg: `These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant adverts on other sites. They do not store directly personal information, but are based on uniquely identifying your browser and internet device. If you do not allow these cookies, you will experience less targeted advertising.`,
          buttons: [ 'Ok' ],
          primary: 'Ok',
          persistent: false
        })"
        v-html="mdiHelpCircle"
      />
    </div>

    <div class="ma-n2">
      <v-btn class="ma-2" color="primary" :disabled="!canUpdate" @click="udpateCookies">{{global.cookies ? 'Update' : 'Accept'}}</v-btn>
      <!-- <v-btn class="ma-2" @click="resetCookies">Reset all</v-btn> -->
    </div>

  </v-container>
</template>

<style scoped>
.card-caption {
  max-width: unset;
  position: absolute;
  left: -10px;
  right: -10px;
  bottom: -10px;
}
</style>

<script>
import store from '@/utils/local-storage.js';
import { mdiAlert, mdiHelpCircle } from '@mdi/js';

const isStoreAvailable = store.isAvailable();

export default {
  name: 'cookies',

  routerViewMeta: {
    title: 'Cookie manager',
    description: 'Manage what cookies Ellacard can store on your browser'
  },

  data() {
    return {
      scope: { strict: isStoreAvailable, targeting: isStoreAvailable, performance: isStoreAvailable }
    };
  },

  computed: {
    disabled() {
      return !isStoreAvailable;
    },

    canUpdate() {
      const g = this.global.cookies;
      const s = this.scope;
      return !this.disabled && (!g || (s.targeting != g.targeting) || (s.performance != g.performance));
    }
  },

  created() {
    Object.assign(this, { mdiAlert, mdiHelpCircle });
  },

  methods: {
    udpateCookies() {
      this.global.cookies = { ...this.scope };
    }
  },

  watch: {
    'global.cookies': {
      immediate: true,
      handler(v) {
        if (v)
          this.scope = { ...v };
      }
    }
  }
}
</script>
